import { createContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

// project imports
import { useLocalStorage } from 'hooks/useLocalStorage';
import { selectCurrentUser } from 'features/auth/authSlice';
import { IDIOMAS, IDIOMA_POR_DEFECTO, intlLocaleDe, resolveLocale } from './index';
import { translate } from './translate';

// ==============================|| I18N - PROVEEDOR ||============================== //
// Un único proveedor para todo el idioma de la aplicación: no hay un segundo
// mecanismo de textos ni banderas `language === 'es' ? ... : ...` repartidas por
// las pantallas. Todo texto pasa por `t('clave')`.
//
// Precedencia de la preferencia:
//   1. la que devuelve el backend en el perfil (`user.language`), cuando existe;
//   2. la última elegida en este navegador (localStorage);
//   3. el idioma por defecto.
//
// El backend manda porque la preferencia es del usuario, no del dispositivo: al
// entrar desde otro equipo debe verse el mismo idioma. Mientras el perfil no
// traiga `language` (ver nota de contrato más abajo), rige lo guardado aquí, que
// es lo que hace que la elección sobreviva a un recargado de página.

export const STORAGE_KEY = 'cobranza-idioma';

export const I18nContext = createContext(undefined);

export function I18nProvider({ children }) {
  const { state: localeGuardado, setState: guardarLocale } = useLocalStorage(STORAGE_KEY, IDIOMA_POR_DEFECTO);
  const usuario = useSelector(selectCurrentUser);

  const locale = resolveLocale(localeGuardado);

  // Solo se adopta la preferencia del backend cuando *cambia*, no en cada
  // render: de lo contrario una elección manual del usuario sería revertida al
  // instante por el perfil ya cargado en memoria.
  const ultimaPreferenciaAplicada = useRef(null);
  const preferenciaBackend = usuario?.language ?? null;

  useEffect(() => {
    if (!preferenciaBackend || preferenciaBackend === ultimaPreferenciaAplicada.current) return;

    ultimaPreferenciaAplicada.current = preferenciaBackend;
    guardarLocale(resolveLocale(preferenciaBackend));
  }, [preferenciaBackend, guardarLocale]);

  // Mantiene sincronizado el idioma del documento: de él dependen los lectores
  // de pantalla y la separación silábica del navegador.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback(
    (code) => {
      guardarLocale(resolveLocale(code));

      // NOTA DE CONTRATO: el idioma se guarda hoy solo en este navegador.
      // Persistirlo en el perfil requiere que el backend exponga `language` en
      // UserResponseDto y lo acepte en PATCH /usuarios/:id (hoy su
      // ValidationPipe corre con forbidNonWhitelisted y rechazaría el campo).
      // Cuando exista, la llamada va aquí y en ningún otro sitio.
    },
    [guardarLocale]
  );

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      idiomas: IDIOMAS,
      intlLocale: intlLocaleDe(locale),
      t: (key, params) => translate(locale, key, params)
    }),
    [locale, setLocale]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
